import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, ArrowRight, ArrowLeft, Phone, Lock, ShieldCheck } from "lucide-react";
import { getFirebaseAuth } from "@/lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

/**
 * Firebase-powered "forgot password" flow.
 *
 *   step 1 → user enters their registered phone in E.164 format
 *   step 2 → Firebase SMS's a 6-digit OTP (via invisible reCAPTCHA)
 *   step 3 → user enters OTP + new password
 *
 * We never send the OTP to our backend. Instead, Firebase gives us an ID
 * token proving ownership of the phone; we forward that + the new password
 * to /api/auth/verify-firebase-otp which verifies the token server-side.
 */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("+91");
  const [otp, setOtp] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  // `null` = still probing the server for OTP transport availability; `true`/`false` = known.
  // Distinguishing the two lets us avoid a split-second amber "not configured"
  // flash while /auth/otp-config is in flight.
  const [firebaseAvailable, setFirebaseAvailable] = useState(null);
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  useEffect(() => {
    api.get("/auth/otp-config")
      .then(({ data }) => setFirebaseAvailable(!!data.firebase))
      .catch(() => setFirebaseAvailable(false));
  }, []);

  const clearRecaptcha = useCallback(() => {
    try { recaptchaVerifierRef.current?.clear(); } catch { /* ignore */ }
    recaptchaVerifierRef.current = null;
    if (recaptchaContainerRef.current) recaptchaContainerRef.current.innerHTML = "";
  }, []);

  // Fresh DOM node per attempt avoids "reCAPTCHA has already been rendered in this element".
  const ensureRecaptcha = useCallback(() => {
    clearRecaptcha();
    const el = document.createElement("div");
    recaptchaContainerRef.current.appendChild(el);
    recaptchaVerifierRef.current = new RecaptchaVerifier(getFirebaseAuth(), el, { size: "invisible" });
    return recaptchaVerifierRef.current;
  }, [clearRecaptcha]);

  useEffect(() => clearRecaptcha, [clearRecaptcha]);

  const [setupError, setSetupError] = useState(null);

  const sendOtp = async (e) => {
    e.preventDefault();
    setSetupError(null);
    if (!firebaseAvailable) {
      toast.error("OTP transport is not configured on the server.");
      return;
    }
    setBusy(true);
    try {
      const auth = getFirebaseAuth();
      const verifier = ensureRecaptcha();
      const result = await signInWithPhoneNumber(auth, phone.trim(), verifier);
      setConfirmationResult(result);
      setStep(2);
      toast.success("OTP sent to your phone");
    } catch (err) {
      const code = err?.code || "";
      const msg = err?.message || "Failed to send OTP";
      // Actionable guidance for the most common Firebase misconfigurations
      // — otherwise the raw SDK error is cryptic for retail-shop admins.
      if (code === "auth/configuration-not-found" || /configuration-not-found/i.test(msg)) {
        setSetupError({
          title: "Phone Authentication is not enabled",
          steps: [
            "Open Firebase Console → apexmotorgarage-76233",
            "Build → Authentication → Sign-in method",
            "Click 'Phone' → toggle Enable → Save",
            "Refresh this page and try again",
          ],
        });
        toast.error("Phone provider not enabled in Firebase Console");
      } else if (code === "auth/unauthorized-domain" || /unauthorized-domain/i.test(msg)) {
        setSetupError({
          title: "This domain is not authorized",
          steps: [
            "Open Firebase Console → apexmotorgarage-76233",
            "Authentication → Settings → Authorized domains → Add domain",
            `Add: ${window.location.hostname}`,
            "Refresh this page and try again",
          ],
        });
        toast.error("Domain not in Firebase authorized list");
      } else if (code === "auth/invalid-phone-number") {
        toast.error("Invalid phone number. Include country code, e.g. +918921211564");
      } else if (msg.includes("recaptcha")) {
        toast.error("Could not verify browser. Refresh and try again.");
      } else if (code === "auth/too-many-requests" || /too-many-requests/i.test(msg)) {
        toast.error("Too many attempts. Wait a few minutes and try again.");
      } else {
        toast.error(msg);
      }
      clearRecaptcha();
    } finally {
      setBusy(false);
    }
  };

  const verifyAndReset = async (e) => {
    e.preventDefault();
    if (newPass.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPass !== confirmPass) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const credential = await confirmationResult.confirm(otp.trim());
      const idToken = await credential.user.getIdToken();
      await api.post("/auth/verify-firebase-otp", {
        firebase_id_token: idToken,
        new_password: newPass,
      });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err) || "Invalid or expired OTP");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="forgot-page">
      <div className="hidden lg:flex relative bg-zinc-950 text-white flex-col justify-between p-12 overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 bg-white text-zinc-950 flex items-center justify-center">
            <Wrench className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Recovery</div>
            <div className="font-bold text-lg tracking-tight">PARTSGRID</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6 max-w-md">
          <div className="label-eyebrow text-zinc-400">Firebase Phone Verification</div>
          <h1 className="text-5xl font-bold leading-[1.05] tracking-tight">Reset your password with a one-time code.</h1>
          <p className="text-zinc-400 text-base leading-relaxed">
            We'll send a 6-digit code to your registered phone number via SMS.
            Enter it here and set a new password.
          </p>
        </div>
        <div className="relative z-10 font-mono text-[11px] text-zinc-500 uppercase tracking-[0.15em]">
          Secure OTP · Powered by Firebase Phone Auth
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-6">
          <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>

          <div className="space-y-2">
            <div className="label-eyebrow">Reset password · Step {step} of 2</div>
            <h2 className="text-3xl font-bold tracking-tight text-zinc-950">
              {step === 1 ? "Enter your number" : "Verify and set new password"}
            </h2>
            <p className="text-sm text-zinc-500">
              {step === 1 ? "We'll SMS a one-time code to this phone." : `Code sent to ${phone}. It expires in 10 minutes.`}
            </p>
          </div>

          {firebaseAvailable === false && (
            <div className="border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 font-mono" data-testid="firebase-not-configured">
              OTP transport not yet configured. Contact your administrator.
            </div>
          )}

          {setupError && (
            <div className="border border-rose-300 bg-rose-50 text-rose-950 text-xs px-4 py-3" data-testid="firebase-setup-error">
              <div className="font-semibold uppercase tracking-wider text-[11px] mb-2 text-rose-800">
                Firebase setup required: {setupError.title}
              </div>
              <ol className="list-decimal list-inside space-y-1 font-mono text-[11px] leading-relaxed">
                {setupError.steps.map((s) => <li key={s}>{s}</li>)}
              </ol>
              <a
                href="https://console.firebase.google.com/project/apexmotorgarage-76233/authentication/providers"
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-2 text-[11px] uppercase tracking-wider text-rose-800 hover:underline"
              >
                Open Firebase Console →
              </a>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={sendOtp} className="space-y-4" data-testid="forgot-form-step1">
              <div>
                <label className="label-eyebrow mb-2 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone (E.164)</label>
                <input
                  data-testid="forgot-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-white border border-zinc-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
                  placeholder="+918921211564"
                  required
                  autoFocus
                />
                <p className="text-[10px] font-mono text-zinc-500 mt-1 uppercase tracking-wider">
                  Include country code with a leading +
                </p>
              </div>
              <button
                type="submit"
                disabled={busy || !firebaseAvailable}
                data-testid="forgot-send-otp-button"
                className="group w-full bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold py-3 hover:bg-zinc-800 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? "Sending..." : "Send code"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyAndReset} className="space-y-4" data-testid="forgot-form-step2">
              <div>
                <label className="label-eyebrow mb-2 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verification code</label>
                <input
                  data-testid="forgot-otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full bg-white border border-zinc-300 text-lg tracking-[0.4em] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono text-center"
                  placeholder="000000"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="label-eyebrow mb-2 flex items-center gap-1"><Lock className="w-3 h-3" /> New password</label>
                <input
                  data-testid="forgot-newpass-input"
                  type="password"
                  minLength={8}
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  className="w-full bg-white border border-zinc-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
                  placeholder="At least 8 characters"
                  required
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Confirm new password</label>
                <input
                  data-testid="forgot-confirmpass-input"
                  type="password"
                  minLength={8}
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  className="w-full bg-white border border-zinc-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-verify-button"
                className="group w-full bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold py-3 hover:bg-zinc-800 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? "Verifying..." : "Reset password"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setOtp(""); setConfirmationResult(null); }}
                className="w-full text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
              >
                Send code to a different number
              </button>
            </form>
          )}

          {/* Invisible reCAPTCHA anchor — Firebase attaches a widget here. */}
          <div ref={recaptchaContainerRef} data-testid="recaptcha-anchor" />
        </div>
      </div>
    </div>
  );
}
